import { fetchConfig, lockScroll, unlockScroll } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { CARD_MAX, validateCardMessage } from '@pt/pt-card-rules';

/**
 * Product-page behaviour for the Petit Toi (pt-*) blocks: option picker,
 * names, photo upload, card message modal, make-it-special add-ons,
 * delivery estimate, and the single add-to-cart request that replaces
 * Horizon's native (single-variant) submit on pt-* templates.
 * @module pt-product
 */

/** Matches a Shopify line-item property field name, e.g. `properties[Notes]`. */
const PROPERTY_NAME_PATTERN = /^properties\[(.+)\]$/;

/** @typedef {{id:number, options:string[], price:number, available:boolean, faces:number, pack_saving:number, unit_label:string, card_caption:string, badge_label:string}} PtVariant */

/** Currently selected variant, shared across the option picker and the totals it drives. */
let currentVariant = /** @type {PtVariant | null} */ (null);
/** The doll line's own quantity, from the optional "for more than one?" stepper (default 1 when the stepper isn't shown). */
let currentQuantity = 1;
/** Sets the doll quantity through the stepper (null when the stepper isn't rendered). */
let setDollQuantity = /** @type {((quantity: number) => void) | null} */ (null);
/**
 * `_doll_id` of the cart doll being edited, set by {@link initEditMode}. A
 * successful add removes that doll and its add-ons, so saving replaces the
 * doll instead of adding a second one.
 */
let editingDollId = '';
let moneyFormat = '${{amount}}';
let currencyCode = 'USD';

/**
 * The current product's own `#ProductInformation-<section.id>` element,
 * resolved once by {@link init}. Every DOM read/write that reacts to option
 * changes — including the `pt:variant-change` event bus — is scoped to this
 * element (or, for the sticky add-to-cart bar, to its enclosing
 * `.shopify-section`) so a page that renders more than one product —
 * recommendations, cross-sells — never touches another product's markup.
 */
let productScope = /** @type {Element | null} */ (null);

/**
 * Recomputes the running total (variant × quantity + chosen add-ons +
 * non-English card language add-on -- add-ons are priced once per order,
 * not multiplied by the doll's own quantity) and writes it into the header
 * price and every add-to-cart button on the page. The header's own unit
 * price normally stays unmultiplied, matching K (quantity only changes the
 * button's total, never the displayed unit price) -- except when
 * pt-price-line.liquid's own custom.price_unit_mode is "quantity" (the
 * couple set): there, K's own header price genuinely is unit price ×
 * quantity ("$198 · 2 dolls", not "$99 · 2 dolls"), since the stepper's
 * quantity is submitted on that same variant, so it really is what the
 * shopper pays. updateHeaderPrice reads the mode itself from the price
 * line's own `data-unit-mode` attribute rather than duplicating that
 * decision here.
 */
function refreshTotal() {
  const variantPrice = currentVariant ? currentVariant.price : 0;
  const totalCents = variantPrice * currentQuantity + computeAddonsTotalCents();
  updateHeaderPrice(variantPrice);
  updateButtonTotals(totalCents);
  updateStickyAddToCart(totalCents);
}

/**
 * Formats money the way the `money_without_trailing_zeros` Liquid filter
 * does: no decimals for a whole-currency-unit amount ($99), decimals kept
 * otherwise ($24.75). Swaps the format string's `{{amount}}` placeholder for
 * `{{amount_no_decimals}}` when whole, then defers to the shared formatter
 * for the actual currency-aware separators/precision.
 * @param {number} cents
 * @param {string} format
 * @param {string} currency
 */
function formatMoneyNoTrailingZeros(cents, format, currency) {
  const isWhole = cents % 100 === 0;
  return formatMoney(cents, isWhole ? format.replace('{{amount}}', '{{amount_no_decimals}}') : format, currency);
}

/**
 * @param {number} cents
 */
function updateHeaderPrice(cents) {
  if (!productScope) return;

  // pt-price-line.liquid (blocks/pt-price-line.liquid): K's "$99 · or 4 ×
  // $24.75 with Shop Pay" line, replacing Horizon's native price block.
  // "quantity" unit-mode multiplies by the doll's own quantity (see
  // refreshTotal's doc comment); every other mode (or none) shows the
  // unmultiplied unit price.
  const unitEl = /** @type {HTMLElement | null} */ (productScope.querySelector('[data-pt-price-unit]'));
  const displayCents = unitEl?.dataset.unitMode === 'quantity' ? cents * currentQuantity : cents;

  const amountEl = productScope.querySelector('[data-pt-price-amount]');
  if (amountEl) amountEl.textContent = formatMoneyNoTrailingZeros(displayCents, moneyFormat, currencyCode);

  const installmentsEl = /** @type {HTMLElement | null} */ (productScope.querySelector('[data-pt-price-installments]'));
  if (installmentsEl) {
    const prefix = installmentsEl.dataset.installmentsPrefix || 'or';
    const suffix = installmentsEl.dataset.installmentsSuffix || 'with Shop Pay';
    const installmentCents = Math.round(displayCents / 4);
    installmentsEl.textContent = `· ${prefix} 4 × ${formatMoneyNoTrailingZeros(installmentCents, moneyFormat, currencyCode)} ${suffix}`;
  }

  updatePriceUnit();
}

/**
 * Keeps pt-price-line.liquid's "· <unit>" text in sync: "variant" mode
 * mirrors the selected variant's own unit_label (K's catalog format pages,
 * e.g. "2 ornaments"); "quantity" mode reads the doll line's own quantity
 * stepper instead (the couple set: "2 dolls"), pluralizing the singular
 * noun the element carries when the count isn't 1. A no-op when the
 * product sets neither price_unit_mode (no `[data-pt-price-unit]` element).
 */
function updatePriceUnit() {
  if (!productScope) return;
  const unitEl = /** @type {HTMLElement | null} */ (productScope.querySelector('[data-pt-price-unit]'));
  if (!unitEl) return;

  const mode = unitEl.dataset.unitMode;
  if (mode === 'variant') {
    const label = currentVariant?.unit_label;
    if (label) unitEl.textContent = `· ${label}`;
  } else if (mode === 'quantity') {
    const noun = unitEl.dataset.unitNoun || 'doll';
    const pluralNoun = currentQuantity === 1 ? noun : `${noun}s`;
    unitEl.textContent = `· ${currentQuantity} ${pluralNoun}`;
  }
}

/**
 * Horizon's sticky add-to-cart bar (sections/product-information.liquid)
 * renders its own variant title + `{% render 'price' %}` as a sibling of
 * `_product-details`, outside `productScope` -- kept in sync from here
 * instead of editing sticky-add-to-cart.js: that component only re-renders
 * the bar from a Section-Rendering-API `StandardEvents.productSelect`
 * promise, which our custom pt-option-picker never dispatches (it uses its
 * own `pt:variant-change` bus instead, to avoid a server round trip per
 * option change). Left unwired, the bar is stuck showing whichever variant
 * Liquid rendered first, at its unit price only -- this fixes both: the
 * variant label from the same `options` the picker already resolved (joined
 * the way Shopify's own default variant title is, " / "-separated), and the
 * price at the *order* total (variant × quantity + add-ons), matching the
 * main Add to cart button's total -- not just the unit price -- since K's
 * own mobile sticky bar shows the decimal cart total ($108.95), never a
 * trimmed unit price.
 * @param {number} totalCents
 */
function updateStickyAddToCart(totalCents) {
  if (!productScope) return;

  const pageSection = productScope.closest('.shopify-section');
  const stickyEl = /** @type {HTMLElement | null} */ (pageSection?.querySelector('sticky-add-to-cart') ?? null);
  if (!stickyEl) return;

  stickyEl.dataset.variantAvailable = String(Boolean(currentVariant?.available));
  if (currentVariant) stickyEl.dataset.currentVariantId = String(currentVariant.id);

  const variantEl = stickyEl.querySelector('.sticky-add-to-cart__variant');
  if (variantEl) {
    variantEl.textContent = currentVariant?.options?.filter(Boolean).join(' / ') || '';
  }

  const formatted = formatMoney(totalCents, moneyFormat, currencyCode);
  stickyEl.querySelectorAll('.sticky-add-to-cart__price [ref="priceContainer"] .price bdi').forEach((el) => {
    el.textContent = formatted;
  });
}

/**
 * @param {number} totalCents
 */
function updateButtonTotals(totalCents) {
  if (!productScope) return;

  productScope.querySelectorAll('button[name="add"]').forEach((button) => {
    let totalEl = /** @type {HTMLElement | null} */ (button.querySelector('[data-pt-total-price]'));
    if (!totalEl) {
      totalEl = document.createElement('span');
      totalEl.dataset.ptTotalPrice = '';
      totalEl.className = 'pt-total-price';
      const addedBurst = button.querySelector('.add-to-cart__added');
      if (addedBurst) {
        button.insertBefore(totalEl, addedBurst);
      } else {
        button.appendChild(totalEl);
      }
    }
    totalEl.textContent = ` – ${formatMoney(totalCents, moneyFormat, currencyCode)}`;
  });
}

/**
 * Sums the price of every checked, visible add-on plus the card-language
 * add-on when a non-English language is selected.
 * @returns {number} total in the shop's minor currency unit (cents)
 */
function computeAddonsTotalCents() {
  let total = 0;

  productScope?.querySelectorAll('[data-pt-addon]').forEach((label) => {
    if (/** @type {HTMLElement} */ (label).hidden) return;
    const checkbox = /** @type {HTMLInputElement | null} */ (label.querySelector('[data-pt-addon-checkbox]'));
    if (!checkbox?.checked) return;

    const select = /** @type {HTMLSelectElement | null} */ (label.querySelector('[data-pt-addon-variant]'));
    if (select) {
      const option = select.selectedOptions[0];
      total += Number(option?.dataset.unitPrice || 0);
    } else {
      const priceEl = /** @type {HTMLElement | null} */ (label.querySelector('[data-pt-addon-price]'));
      total += Number(priceEl?.dataset.unitPrice || 0);
    }
  });

  const cardMessageRoot = productScope?.querySelector('[data-pt-card-message]');
  const languageSelect = /** @type {HTMLSelectElement | null} */ (
    cardMessageRoot?.querySelector('[data-pt-card-language]') ?? null
  );
  if (cardMessageRoot instanceof HTMLElement && languageSelect && languageSelect.value !== 'English') {
    total += Number(cardMessageRoot.dataset.languageAddonPrice || 0);
  }

  return total;
}

/**
 * Wires the option picker: resolves the variant client-side from the inline
 * JSON payload, keeps the form's `id` input, the header price and the
 * button totals in sync, and dispatches `pt:variant-change`.
 */
function initOptionPicker() {
  const root = /** @type {HTMLElement | null} */ (productScope?.querySelector('[data-pt-option-picker]') ?? null);
  if (!root) return;

  const variantsScript = root.querySelector('script[data-pt-variants]');
  /** @type {PtVariant[]} */
  let variants = [];
  try {
    variants = JSON.parse(variantsScript?.textContent || '[]');
  } catch (error) {
    console.error('[pt-product] Could not parse the option picker variant JSON.', error);
    return;
  }
  if (!Array.isArray(variants) || variants.length === 0) return;

  moneyFormat = root.dataset.moneyFormat || moneyFormat;
  currencyCode = root.dataset.currency || currencyCode;

  const formId = root.dataset.productForm;
  const form = formId ? /** @type {HTMLFormElement | null} */ (document.getElementById(formId)) : null;
  const unavailableEl = /** @type {HTMLElement | null} */ (root.querySelector('[data-pt-unavailable]'));
  const optionCount = root.querySelectorAll('[data-pt-option-group]').length;

  /** @returns {(string | null)[]} */
  function getSelectedOptions() {
    const values = /** @type {(string | null)[]} */ (new Array(optionCount).fill(null));
    root.querySelectorAll('[data-pt-option-input]:checked').forEach((input) => {
      const index = Number(/** @type {HTMLElement} */ (input).dataset.optionIndex);
      values[index] = /** @type {HTMLInputElement} */ (input).value;
    });
    return values;
  }

  /**
   * @param {(string | null)[]} values
   * @returns {PtVariant | null}
   */
  function findVariant(values) {
    // Nothing checked yet (every option group unselected): report "no match"
    // instead of the vacuously-true first variant, so callers fall back to
    // `data-initial-variant-id`.
    if (values.length > 0 && values.every((value) => value == null)) return null;
    return (
      variants.find((variant) => values.every((value, index) => value == null || variant.options[index] === value)) ??
      null
    );
  }

  // The step's own "cards" fieldset -- the one option whose values render
  // as price/caption/badge cards, as opposed to a pill row. When a product
  // has other axes (pills, before or after), each card's price/caption/
  // badge must reflect the shopper's *current* selection on those other
  // axes, not a single server-rendered snapshot -- e.g. carry-me-tote's
  // pet-count cards show the Full-set price ($110/$169/$228) while "Full
  // set" is picked, and the Tote-only price ($79) once "Tote only" is
  // picked instead. A no-op (nothing to update) when the product has no
  // cards fieldset (all-pills-no-step case, doesn't exist today) or no
  // other axes to pin (single-option products: each card's own variant
  // never changes, so this only ever re-renders identical values).
  const cardsFieldset = /** @type {HTMLElement | null} */ (root.querySelector('.pt-option-group--cards'));
  const cardsOptionIndex = cardsFieldset ? Number(cardsFieldset.dataset.optionIndex) : null;
  const cardsSaveLabel = cardsFieldset?.dataset.saveLabel || 'save';

  /** @param {(string | null)[]} selectedValues */
  function updateStepCards(selectedValues) {
    if (!cardsFieldset || cardsOptionIndex == null) return;

    cardsFieldset.querySelectorAll('[data-pt-option-input]').forEach((input) => {
      const label = /** @type {HTMLElement | null} */ (input.closest('.pt-option-card'));
      const body = label?.querySelector('.pt-option-card__body');
      if (!body) return;

      const probe = selectedValues.slice();
      probe[/** @type {number} */ (cardsOptionIndex)] = /** @type {HTMLInputElement} */ (input).value;
      const matched = variants.find((variant) =>
        probe.every((value, index) => value == null || variant.options[index] === value)
      );
      if (!matched) return;

      // No "from" prefix here: every other axis is pinned to the shopper's
      // current selection (`probe`), so exactly one variant can match --
      // the ambiguity that prefix communicates on first render (several
      // variants share this card's value) no longer applies once a real
      // combination is selected.
      const priceEl = body.querySelector('[data-pt-card-price]');
      if (priceEl) priceEl.textContent = formatMoneyNoTrailingZeros(matched.price, moneyFormat, currencyCode);

      let badgeEl = body.querySelector('.pt-option-card__badge');
      if (matched.badge_label) {
        if (!badgeEl) {
          badgeEl = document.createElement('span');
          badgeEl.className = 'pt-option-card__badge';
          body.insertBefore(badgeEl, body.firstChild);
        }
        badgeEl.textContent = matched.badge_label;
      } else {
        badgeEl?.remove();
      }

      body.querySelector('.pt-option-card__caption, .pt-option-card__saving')?.remove();
      if (priceEl) {
        let extraEl = null;
        if (matched.card_caption) {
          extraEl = document.createElement('span');
          extraEl.className = 'pt-option-card__caption';
          extraEl.textContent = matched.card_caption;
        } else if (matched.pack_saving > 0) {
          extraEl = document.createElement('span');
          extraEl.className = 'pt-option-card__saving';
          extraEl.textContent = `${cardsSaveLabel} ${matched.pack_saving}%`;
        }
        if (extraEl) priceEl.insertAdjacentElement('afterend', extraEl);
      }
    });
  }

  /** @param {PtVariant | undefined} variant */
  function applyVariant(variant) {
    if (form) {
      const idInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="id"]'));
      if (idInput) idInput.value = variant ? String(variant.id) : '';
    }

    if (unavailableEl) unavailableEl.hidden = !variant || variant.available;

    form
      ?.querySelectorAll('button[name="add"]')
      .forEach((button) => {
        /** @type {HTMLButtonElement} */ (button).disabled = !variant || !variant.available;
      });

    currentVariant = variant ?? null;
    refreshTotal();

    // Dispatched on the product's own scope (not `document`) so a page that
    // ever renders more than one product section only notifies listeners
    // that belong to this product.
    productScope?.dispatchEvent(
      new CustomEvent('pt:variant-change', {
        detail: {
          productId: root.dataset.productId,
          variant,
          optionValues: getSelectedOptions(),
        },
      })
    );
  }

  root.querySelectorAll('[data-pt-option-input]').forEach((input) => {
    input.addEventListener('change', () => {
      const selectedValues = getSelectedOptions();
      applyVariant(findVariant(selectedValues));
      updateStepCards(selectedValues);
    });
  });

  initQuantityStepper(root);

  // bfcache / back-navigation can restore radios to a selection that no
  // longer matches the server-rendered `data-initial-variant-id` (the
  // shopper picked a different option, then navigated away and back).
  // Trust the DOM's actual checked state first, and only fall back to the
  // server-rendered initial variant when nothing is checked.
  const initialSelectedValues = getSelectedOptions();
  const initialVariant =
    findVariant(initialSelectedValues) ??
    variants.find((variant) => String(variant.id) === root.dataset.initialVariantId) ??
    variants[0];
  applyVariant(initialVariant);
  updateStepCards(initialSelectedValues);
}

/**
 * Wires the optional "for more than one?" quantity stepper: keeps
 * `currentQuantity`, the visible count and the hidden `quantity` input in
 * sync, and re-runs {@link refreshTotal}. A no-op when the block setting
 * that renders it is off (no `[data-pt-qty-row]` in the DOM).
 * @param {HTMLElement} optionPickerRoot
 */
function initQuantityStepper(optionPickerRoot) {
  const row = optionPickerRoot.querySelector('[data-pt-qty-row]');
  if (!row) {
    currentQuantity = 1;
    return;
  }

  const valueEl = /** @type {HTMLElement | null} */ (row.querySelector('[data-pt-qty-value]'));
  const hiddenInput = /** @type {HTMLInputElement | null} */ (row.querySelector('[data-pt-qty-input]'));
  const decreaseButton = row.querySelector('[data-pt-qty-decrease]');
  const increaseButton = row.querySelector('[data-pt-qty-increase]');
  const max = Number(hiddenInput?.max) || 5;

  function setQuantity(next) {
    currentQuantity = Math.max(1, Math.min(max, Math.round(next) || 1));
    if (valueEl) valueEl.textContent = String(currentQuantity);
    if (hiddenInput) hiddenInput.value = String(currentQuantity);
    if (decreaseButton instanceof HTMLButtonElement) decreaseButton.disabled = currentQuantity <= 1;
    if (increaseButton instanceof HTMLButtonElement) increaseButton.disabled = currentQuantity >= max;
    refreshTotal();
  }

  setDollQuantity = setQuantity;
  decreaseButton?.addEventListener('click', () => setQuantity(currentQuantity - 1));
  increaseButton?.addEventListener('click', () => setQuantity(currentQuantity + 1));

  setQuantity(Number(hiddenInput?.value) || 1);
}

/**
 * Wires the name field(s): shows one input per face for the current variant
 * and joins non-empty values into the single hidden `Name` property.
 */
function initNames() {
  const root = /** @type {HTMLElement | null} */ (productScope?.querySelector('[data-pt-names]') ?? null);
  if (!root) return;

  const fieldsContainer = root.querySelector('[data-pt-names-fields]');
  const hiddenInput = /** @type {HTMLInputElement | null} */ (root.querySelector('[data-pt-name-hidden]'));
  if (!fieldsContainer || !hiddenInput) return;

  const maxFaces = Number(root.dataset.maxFaces || 5);
  const labelPrefix = root.dataset.labelPrefix || 'Name';

  function syncHidden() {
    const values = Array.from(fieldsContainer.querySelectorAll('[data-pt-name-input]'))
      .map((input) => /** @type {HTMLInputElement} */ (input).value.trim())
      .filter(Boolean);
    hiddenInput.value = values.join(', ');
  }

  /** @param {number} count */
  function setFaceCount(count) {
    const clamped = Math.max(1, Math.min(maxFaces, Math.round(count) || 1));
    const existingValues = Array.from(fieldsContainer.querySelectorAll('[data-pt-name-input]')).map(
      (input) => /** @type {HTMLInputElement} */ (input).value
    );
    if (existingValues.length === clamped) return;

    fieldsContainer.innerHTML = '';
    for (let i = 0; i < clamped; i += 1) {
      const label = document.createElement('label');
      label.className = 'pt-names__field';

      const srLabel = document.createElement('span');
      srLabel.className = 'visually-hidden';
      srLabel.textContent = clamped > 1 ? `${labelPrefix} ${i + 1}` : labelPrefix;

      const input = document.createElement('input');
      input.type = 'text';
      input.className = 'pt-names__input';
      input.dataset.ptNameInput = '';
      input.maxLength = 60;
      input.placeholder = clamped > 1 ? `${labelPrefix} ${i + 1}` : labelPrefix;
      input.value = existingValues[i] || '';
      input.addEventListener('input', syncHidden);

      label.append(srLabel, input);
      fieldsContainer.appendChild(label);
    }
    syncHidden();
  }

  fieldsContainer.querySelectorAll('[data-pt-name-input]').forEach((input) => {
    input.addEventListener('input', syncHidden);
  });
  syncHidden();

  productScope?.addEventListener('pt:variant-change', (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    setFaceCount(detail?.variant?.faces ?? Number(root.dataset.initialFaces || 1));
  });
}

/**
 * Wires the photo-upload step. "Has a photo, or ticked 'I'll send photos
 * later'" is mirrored onto the checkbox's native validity
 * (setCustomValidity) so the browser's own form validation enforces it:
 * Horizon's AddToCartComponent.handleClick only plays its "added" and
 * fly-to-cart animations when `form.checkValidity()` passes, and the
 * interactive validation that runs before `submit` fires `invalid` instead
 * of submitting -- so an invalid click never animates and never posts.
 * {@link validatePhotoUpload} still re-checks at submit time for the paths
 * that don't go through native validation ("Buy with shop").
 */
function initPhotoUpload() {
  const root = /** @type {HTMLElement | null} */ (productScope?.querySelector('[data-pt-photo-upload]') ?? null);
  if (!root) return;

  const laterCheckbox = /** @type {HTMLInputElement | null} */ (root.querySelector('[data-pt-photos-later]'));
  const form = laterCheckbox?.form ?? null;

  if (laterCheckbox && form) {
    const sync = () => {
      if (syncPhotoValidity(root, laterCheckbox, form)) hidePhotoError(root);
    };
    sync();
    // The upload app's fields can live outside this block (a sibling block,
    // tied to the form only by `form=`), so their events never bubble
    // through `root` or `form` -- listen on `window` instead. The click
    // listener re-syncs right before any add-to-cart click is judged, in
    // case an app writes its value without dispatching an event; capture on
    // `window` runs ahead of Horizon's own document-level click dispatch.
    window.addEventListener('change', sync, true);
    window.addEventListener('input', sync, true);
    window.addEventListener('click', sync, true);

    laterCheckbox.addEventListener('invalid', (event) => {
      event.preventDefault();
      showPhotoError(root);
    });
  }

  // Dropzone placeholder (blocks/pt-photo-upload.liquid): the real upload
  // app isn't installed yet, so a click can't pretend to upload -- it's
  // honest about that (a status message) and points at the one thing that
  // actually works today, "I'll send photos later".
  const placeholder = root.querySelector('[data-pt-photo-placeholder]');
  const placeholderStatus = /** @type {HTMLElement | null} */ (root.querySelector('[data-pt-photo-placeholder-status]'));
  placeholder?.addEventListener('click', () => {
    if (placeholderStatus) {
      placeholderStatus.textContent = /** @type {HTMLElement} */ (placeholder).dataset.message || '';
      placeholderStatus.hidden = false;
    }
    laterCheckbox?.focus();
  });
}

/**
 * Whether the photo step is satisfied: "I'll send photos later" is ticked,
 * or a line-item property matching the block's photo pattern has a value.
 * @param {HTMLElement} root - the `[data-pt-photo-upload]` element
 * @param {HTMLFormElement} form
 * @returns {boolean}
 */
function hasPhotoOrLater(root, form) {
  const laterCheckbox = /** @type {HTMLInputElement | null} */ (root.querySelector('[data-pt-photos-later]'));
  if (laterCheckbox?.checked) return true;

  const pattern = new RegExp(root.dataset.photoPattern || 'photo|upload|file', 'i');
  for (const [name, value] of new FormData(form).entries()) {
    const match = name.match(PROPERTY_NAME_PATTERN);
    if (!match) continue;
    const key = match[1];
    if (key === 'Photos later') continue;
    if (!pattern.test(key)) continue;

    if (value instanceof File ? value.name : typeof value === 'string' && value.trim() !== '') return true;
  }
  return false;
}

/**
 * Mirrors {@link hasPhotoOrLater} onto the checkbox's native validity.
 * @param {HTMLElement} root
 * @param {HTMLInputElement} laterCheckbox
 * @param {HTMLFormElement} form
 * @returns {boolean} whether the step is satisfied
 */
function syncPhotoValidity(root, laterCheckbox, form) {
  const ok = hasPhotoOrLater(root, form);
  laterCheckbox.setCustomValidity(ok ? '' : photoErrorMessage(root));
  return ok;
}

/** @param {HTMLElement} root */
function photoErrorMessage(root) {
  return root.dataset.errorMessage || "Please add a photo, or check “I'll send photos later”.";
}

/**
 * Shows the photo-step error, highlights the step, and brings it into view
 * with focus on the "I'll send photos later" checkbox.
 * @param {HTMLElement} root
 */
function showPhotoError(root) {
  const errorEl = /** @type {HTMLElement | null} */ (root.querySelector('[data-pt-photo-error]'));
  if (errorEl) {
    errorEl.textContent = photoErrorMessage(root);
    errorEl.hidden = false;
  }

  // Restart the attention animation on every failed attempt, not just the first.
  root.classList.remove('pt-photo-upload--invalid');
  void root.offsetWidth;
  root.classList.add('pt-photo-upload--invalid');

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  root.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
  /** @type {HTMLInputElement | null} */ (root.querySelector('[data-pt-photos-later]'))?.focus({ preventScroll: true });
}

/** @param {HTMLElement} root */
function hidePhotoError(root) {
  const errorEl = /** @type {HTMLElement | null} */ (root.querySelector('[data-pt-photo-error]'));
  if (errorEl) errorEl.hidden = true;
  root.classList.remove('pt-photo-upload--invalid');
}

/**
 * Validates the photo-upload requirement against the live form, showing
 * the error when it isn't met.
 * @param {HTMLFormElement} form
 * @returns {{ok: boolean}}
 */
function validatePhotoUpload(form) {
  const scope = form.closest('[id^="ProductInformation-"]');
  const root = /** @type {HTMLElement | null} */ (scope?.querySelector('[data-pt-photo-upload]') ?? null);
  if (!root) return { ok: true };

  const ok = hasPhotoOrLater(root, form);
  if (ok) {
    hidePhotoError(root);
  } else {
    showPhotoError(root);
  }
  return { ok };
}

/**
 * Wires the card message tile + modal: live preview, character counter,
 * idea chips, language select, and validation via pt-card-rules.js.
 */
function initCardMessage() {
  const root = /** @type {HTMLElement | null} */ (productScope?.querySelector('[data-pt-card-message]') ?? null);
  if (!root) return;

  const dialog = /** @type {HTMLDialogElement | null} */ (root.querySelector('[data-pt-card-modal]'));
  const trigger = /** @type {HTMLButtonElement | null} */ (root.querySelector('[data-pt-card-trigger]'));
  const textarea = /** @type {HTMLTextAreaElement | null} */ (root.querySelector('[data-pt-card-textarea]'));
  if (!dialog || !trigger || !textarea) return;

  const closeButton = root.querySelector('[data-pt-card-close]');
  const counterEl = root.querySelector('[data-pt-card-counter]');
  const errorEl = /** @type {HTMLElement | null} */ (root.querySelector('[data-pt-card-modal-error]'));
  const modalPreview = root.querySelector('[data-pt-card-modal-preview]');
  const tilePreview = /** @type {HTMLElement | null} */ (root.querySelector('[data-pt-card-preview]'));
  const tileThumb = /** @type {HTMLElement | null} */ (root.querySelector('[data-pt-card-thumb]'));
  const tileFlag = /** @type {HTMLElement | null} */ (root.querySelector('[data-pt-card-tile-flag]'));
  // The in-memory radios are pt-in-memory.liquid's own markup, nested inside
  // this modal (see pt-card-message.liquid's doc comment) -- read by
  // `name`, not a dedicated data-pt-* hook, so pt-in-memory.liquid stays
  // usable standalone with no pt-product.js dependency of its own.
  const inMemoryYesInput = /** @type {HTMLInputElement | null} */ (
    root.querySelector('input[name="properties[In memory]"][value="Yes"]')
  );
  const languageSelect = /** @type {HTMLSelectElement | null} */ (root.querySelector('[data-pt-card-language]'));
  const saveButton = root.querySelector('[data-pt-card-save]');
  const messageInput = /** @type {HTMLInputElement | null} */ (root.querySelector('[data-pt-card-message-input]'));
  const languageInput = /** @type {HTMLInputElement | null} */ (root.querySelector('[data-pt-card-language-input]'));
  // Display-only default (K's own card_example demo copy when the product
  // has one, else the same neutral text prepareDollFormItems falls back to
  // at submit time) -- never confused with dialog.dataset.cardDefault
  // (read directly, only by prepareDollFormItems below), which is what
  // silently fills properties[Card message] and must never be K's example
  // text.
  const cardDisplayDefault = dialog.dataset.cardDisplayDefault || '';
  const tilePlaceholder = tilePreview?.textContent || '';
  const tileThumbPlaceholder = tileThumb?.textContent || '';

  function updateCounter() {
    if (counterEl) counterEl.textContent = `${textarea.value.length} / ${CARD_MAX}`;
  }

  function updateModalPreview() {
    if (modalPreview) modalPreview.textContent = textarea.value.trim() || cardDisplayDefault;
  }

  /** Mirrors the in-memory radio's current value onto the tile's own
   * read-only flag line (K shows this on the card tile itself; the editable
   * control lives in the modal). Always visible -- swaps between the
   * "checked" state (icon/label/note from data-on-*, e.g. "♡ In memory ·
   * packed gently") and the "unchecked" state (data-off-*, either a
   * catalog-format "🎁 Gift · prices hidden in the box" or a doll-segment
   * "♡ In memory · off", per product -- see pt-card-message.liquid's doc
   * comment). */
  function updateTileFlag() {
    if (!tileFlag) return;
    const checked = Boolean(inMemoryYesInput?.checked);
    const prefix = checked ? 'on' : 'off';
    const icon = tileFlag.dataset[`${prefix}Icon`] || '';
    const label = tileFlag.dataset[`${prefix}Label`] || '';
    const note = tileFlag.dataset[`${prefix}Note`] || '';
    const iconEl = tileFlag.querySelector('[data-flag-icon]');
    const labelEl = tileFlag.querySelector('[data-flag-label]');
    const noteEl = tileFlag.querySelector('[data-flag-note]');
    if (iconEl) iconEl.textContent = icon;
    if (labelEl) labelEl.textContent = label;
    if (noteEl) noteEl.textContent = note;
  }

  /** @returns {boolean} */
  function runValidation() {
    const result = validateCardMessage(textarea.value);
    if (errorEl) {
      errorEl.textContent = result.error;
      errorEl.hidden = result.ok;
    }
    textarea.setAttribute('aria-invalid', String(!result.ok));
    return result.ok;
  }

  textarea.addEventListener('input', () => {
    updateCounter();
    updateModalPreview();
    runValidation();
  });

  root.querySelectorAll('[data-pt-card-chip]').forEach((chip) => {
    chip.addEventListener('click', () => {
      textarea.value = chip.textContent?.trim() || '';
      textarea.dispatchEvent(new Event('input', { bubbles: true }));
      textarea.focus();
    });
  });

  trigger.addEventListener('click', () => {
    dialog.showModal();
    textarea.focus();
  });

  closeButton?.addEventListener('click', () => dialog.close());

  dialog.addEventListener('close', () => {
    trigger.focus();
  });

  saveButton?.addEventListener('click', () => {
    if (!runValidation()) {
      textarea.focus();
      return;
    }
    if (messageInput) messageInput.value = textarea.value.trim();
    if (languageInput) languageInput.value = languageSelect?.value || 'English';
    const savedText = textarea.value.trim() || tilePlaceholder;
    if (tilePreview) tilePreview.textContent = savedText;
    if (tileThumb) tileThumb.textContent = textarea.value.trim() || tileThumbPlaceholder;
    updateTileFlag();
    dialog.close();
    refreshTotal();
  });

  languageSelect?.addEventListener('change', () => {
    if (languageInput) languageInput.value = languageSelect.value;
    refreshTotal();
  });

  root.querySelectorAll('input[name="properties[In memory]"]').forEach((input) => {
    input.addEventListener('change', updateTileFlag);
  });

  updateCounter();
  updateModalPreview();
  updateTileFlag();
}

/**
 * Wires the add-on info panel: one shared dialog filled from each row's
 * <template>. The Add/Remove button clicks the row's real checkbox, so the
 * summary, running total and cart payload follow the same `change` path as a
 * direct click.
 * @param {HTMLElement} root the make-it-special <details>
 */
function initAddonInfoDialog(root) {
  const dialog = /** @type {HTMLDialogElement | null} */ (root.querySelector('[data-pt-addon-dialog]'));
  if (!dialog || typeof dialog.showModal !== 'function') return;

  const content = /** @type {HTMLElement} */ (dialog.querySelector('[data-pt-addon-dialog-content]'));
  const toggle = /** @type {HTMLButtonElement} */ (dialog.querySelector('[data-pt-addon-dialog-toggle]'));
  const note = /** @type {HTMLElement} */ (dialog.querySelector('[data-pt-addon-dialog-note]'));
  const closeButton = dialog.querySelector('[data-pt-addon-dialog-close]');
  if (!content || !toggle || !note) return;

  /** @type {HTMLInputElement | null} */
  let activeCheckbox = null;
  /** @type {HTMLElement | null} */
  let activeRow = null;
  /** @type {HTMLElement | null} */
  let opener = null;

  function rowPriceCents() {
    const select = /** @type {HTMLSelectElement | null} */ (activeRow?.querySelector('[data-pt-addon-variant]') ?? null);
    if (select) return Number(select.selectedOptions[0]?.dataset.unitPrice || 0);
    const priceEl = /** @type {HTMLElement | null} */ (activeRow?.querySelector('[data-pt-addon-price]') ?? null);
    return Number(priceEl?.dataset.unitPrice || 0);
  }

  function priceText() {
    return formatMoney(rowPriceCents(), moneyFormat, currencyCode);
  }

  function sync() {
    if (!activeCheckbox) return;
    const on = activeCheckbox.checked;
    const { addLabel = 'Add', addedLabel = 'Added', removeLabel = 'Remove' } = dialog.dataset;
    toggle.toggleAttribute('data-added', on);
    if (on) {
      const tick = document.createElement('span');
      tick.setAttribute('aria-hidden', 'true');
      tick.textContent = '\u2713';
      toggle.replaceChildren(`${addedLabel} `, tick, ` ${removeLabel}`);
    } else {
      toggle.replaceChildren(`${addLabel} +${priceText()}`);
    }
    const unavailable = activeCheckbox.disabled && !on;
    toggle.disabled = unavailable;
    note.hidden = !unavailable;
    note.textContent = unavailable ? root.dataset.rushNote || '' : '';
    if (unavailable && !note.textContent) note.hidden = true;
  }

  root.querySelectorAll('[data-pt-addon-info]').forEach((button) => {
    button.addEventListener('click', (event) => {
      event.preventDefault();
      const row = /** @type {HTMLElement | null} */ (button.closest('[data-pt-addon]'));
      const template = /** @type {HTMLTemplateElement | null} */ (row?.querySelector('template[data-pt-addon-panel]') ?? null);
      const checkbox = /** @type {HTMLInputElement | null} */ (row?.querySelector('[data-pt-addon-checkbox]') ?? null);
      if (!row || !template || !checkbox) return;

      activeRow = row;
      activeCheckbox = checkbox;
      opener = /** @type {HTMLElement} */ (button);
      content.replaceChildren(template.content.cloneNode(true));
      const priceEl = content.querySelector('[data-pt-addon-dialog-price]');
      if (priceEl) priceEl.textContent = `+${priceText()}`;
      sync();
      lockScroll(dialog);
      dialog.showModal();
      dialog.scrollTop = 0;
      closeButton instanceof HTMLElement && closeButton.focus();
    });
  });

  toggle.addEventListener('click', () => {
    if (!activeCheckbox || activeCheckbox.disabled) return;
    activeCheckbox.click();
    sync();
  });

  // Row changes that happen while the panel is open (e.g. the variant select
  // cannot, but keep the label honest if the checkbox is changed elsewhere).
  root.addEventListener('change', () => {
    if (dialog.open) sync();
  });

  closeButton?.addEventListener('click', () => dialog.close());

  dialog.addEventListener('click', (event) => {
    if (event.target !== dialog) return;
    const rect = dialog.getBoundingClientRect();
    const inside =
      event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
    if (!inside) dialog.close();
  });

  dialog.addEventListener('close', () => {
    unlockScroll(dialog);
    content.replaceChildren();
    const target = opener;
    activeCheckbox = null;
    activeRow = null;
    opener = null;
    target?.focus();
  });
}

/**
 * Wires the make-it-special add-on list: summary line, running total, and
 * the rush add-on's size guard.
 */
function initMakeItSpecial() {
  const root = /** @type {HTMLElement | null} */ (productScope?.querySelector('[data-pt-make-it-special]') ?? null);
  if (!root) return;

  const summaryEl = /** @type {HTMLElement | null} */ (root.querySelector('[data-pt-addons-summary]'));
  const summaryDefaultText = summaryEl?.textContent || '';

  function updateSummary() {
    if (!summaryEl) return;
    const checked = Array.from(root.querySelectorAll('[data-pt-addon-checkbox]')).filter((checkbox) => {
      const label = /** @type {HTMLElement | null} */ (checkbox.closest('[data-pt-addon]'));
      return /** @type {HTMLInputElement} */ (checkbox).checked && label && !label.hidden;
    });

    // Nothing checked: the server text names the pre-checked default add-on
    // as "added", which is no longer true once it is unchecked.
    if (checked.length === 0) {
      summaryEl.textContent = root.dataset.summaryEmpty ?? summaryDefaultText;
      return;
    }

    // Still exactly the segment's own default add-on (pre-checked on page
    // load, matching K's own static "<addon> added +$X" wording) and
    // nothing else -- reuse that server-rendered text verbatim instead of
    // falling through to the generic "N added" count below, which only
    // needs to describe a state K's own mockup never shows.
    const isOnlyDefault = checked.length === 1 && checked[0].closest('[data-pt-addon]')?.classList.contains('pt-addon--suggested');
    if (isOnlyDefault) {
      summaryEl.textContent = summaryDefaultText;
      return;
    }

    let total = 0;
    checked.forEach((checkbox) => {
      const label = checkbox.closest('[data-pt-addon]');
      const select = /** @type {HTMLSelectElement | null} */ (label?.querySelector('[data-pt-addon-variant]') ?? null);
      if (select) {
        total += Number(select.selectedOptions[0]?.dataset.unitPrice || 0);
      } else {
        const priceEl = /** @type {HTMLElement | null} */ (label?.querySelector('[data-pt-addon-price]') ?? null);
        total += Number(priceEl?.dataset.unitPrice || 0);
      }
    });

    summaryEl.textContent = `${checked.length} added · +${formatMoney(total, moneyFormat, currencyCode)}`;
  }

  /**
   * Rush eligibility (shared rule with the cart's own guard):
   * - The weekly order cap (`shop.metafields.custom.order_cap_reached`) is
   *   active: rush is never offered, regardless of size — fail closed.
   * - No `Size` option on this product: rush is simply an available add-on
   *   (it only appears here at all because it's in `custom.addons`).
   * - Has a `Size` option but `custom.rush_allowed_sizes` is blank/unparsable:
   *   fail closed — rush is not allowed.
   * - Otherwise: allowed only when the selected size is in the list.
   * @param {(string | null)[]} optionValues
   */
  function updateRushVisibility(optionValues) {
    const rushLabel = /** @type {HTMLElement | null} */ (root.querySelector('[data-pt-addon-rush]'));
    if (!rushLabel) return;

    const capped = root.dataset.capReached === 'true';
    const sizeIndex = Number(root.dataset.sizeOptionIndex);
    let allowed;

    if (capped) {
      allowed = false;
    } else if (sizeIndex < 0) {
      allowed = true;
    } else {
      /** @type {unknown} */
      let allowedSizes = null;
      try {
        allowedSizes = JSON.parse(root.dataset.rushAllowedSizes || 'null');
      } catch (error) {
        allowedSizes = null;
      }

      if (!Array.isArray(allowedSizes) || allowedSizes.length === 0) {
        allowed = false;
      } else {
        const selectedSize = optionValues[sizeIndex];
        allowed = allowedSizes.includes(selectedSize);
      }
    }

    rushLabel.hidden = !allowed;

    const checkbox = /** @type {HTMLInputElement | null} */ (rushLabel.querySelector('[data-pt-addon-checkbox]'));
    if (checkbox) {
      checkbox.disabled = !allowed;
      if (!allowed) checkbox.checked = false;
    }
    updateSummary();
    refreshTotal();
  }

  root.querySelectorAll('[data-pt-addon-checkbox]').forEach((checkbox) => {
    checkbox.addEventListener('change', () => {
      updateSummary();
      refreshTotal();
    });
  });
  root.querySelectorAll('[data-pt-addon-variant]').forEach((select) => {
    select.addEventListener('change', () => {
      updateSummary();
      refreshTotal();
    });
  });

  initAddonInfoDialog(root);

  productScope?.addEventListener('pt:variant-change', (event) => {
    updateRushVisibility(/** @type {CustomEvent} */ (event).detail?.optionValues || []);
  });
}

/**
 * Computes "Arrives <date range>" client-side so cached
 * HTML never shows a stale date.
 */
function initDeliveryLine() {
  const root = /** @type {HTMLElement | null} */ (productScope?.querySelector('[data-pt-delivery-line]') ?? null);
  const textEl = root?.querySelector('[data-pt-delivery-text]');
  if (!root || !textEl) return;

  const productionMin = Number(root.dataset.productionMin || 7);
  const productionMax = Number(root.dataset.productionMax || 14);
  const transitUs = Number(root.dataset.transitUs || 7);
  const transitEu = Number(root.dataset.transitEu || 9);
  const euCountries = (root.dataset.euCountries || '')
    .split(',')
    .map((code) => code.trim())
    .filter(Boolean);
  const country = root.dataset.country || '';
  const locale = root.dataset.locale || undefined;

  const transitDays = euCountries.includes(country) ? transitEu : transitUs;
  const today = new Date();
  const earliest = new Date(today);
  earliest.setDate(earliest.getDate() + productionMin + transitDays);
  const latest = new Date(today);
  latest.setDate(latest.getDate() + productionMax + transitDays);

  const formatter = new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' });
  const rangeText = `${formatter.format(earliest)} – ${formatter.format(latest)}`;

  // K bolds just the date range ("Arrives **Oct 30 – Nov 13**"); shipping
  // cost is left to checkout, which prices it from the address. Built with DOM nodes, not innerHTML, even though rangeText is
  // fully computed (no user input) -- no reason to reach for string HTML
  // when a `<b>` element is this cheap to construct directly.
  textEl.textContent = '';
  textEl.append(`Arrives `);
  const rangeEl = document.createElement('b');
  rangeEl.textContent = rangeText;
  textEl.append(rangeEl);

  const cutoffEl = /** @type {HTMLElement | null} */ (root.querySelector('[data-pt-delivery-cutoff]'));
  const cutoffMonthDay = root.dataset.cutoffMonthDay || '';
  const cutoffMatch = /^(\d{1,2})-(\d{1,2})$/.exec(cutoffMonthDay);
  if (cutoffEl && cutoffMatch) {
    const cutoffDate = new Date(today.getFullYear(), Number(cutoffMatch[1]) - 1, Number(cutoffMatch[2]), 23, 59, 59);
    cutoffEl.hidden = today > cutoffDate;
  }
}

/**
 * @param {HTMLFormElement} form
 * @returns {{[key: string]: string}}
 */
function collectDollProperties(form) {
  const formData = new FormData(form);
  /** @type {{[key: string]: string}} */
  const properties = {};
  for (const [name, value] of formData.entries()) {
    if (typeof value !== 'string') continue;
    const match = name.match(PROPERTY_NAME_PATTERN);
    if (!match) continue;
    const trimmed = value.trim();
    if (trimmed === '') continue;
    properties[match[1]] = trimmed;
  }
  return properties;
}

/**
 * @param {string} dollId
 * @param {Element} scope - the submitting form's own `#ProductInformation-…` element
 * @returns {Array<{id: number, quantity: number, properties: {_for_doll: string}}>}
 */
function collectAddonLines(dollId, scope) {
  /** @type {Array<{id: number, quantity: number, properties: {_for_doll: string}}>} */
  const lines = [];
  scope.querySelectorAll('[data-pt-addon]').forEach((label) => {
    if (/** @type {HTMLElement} */ (label).hidden) return;
    const checkbox = /** @type {HTMLInputElement | null} */ (label.querySelector('[data-pt-addon-checkbox]'));
    if (!checkbox?.checked) return;
    const select = /** @type {HTMLSelectElement | null} */ (label.querySelector('[data-pt-addon-variant]'));
    const variantId = select ? select.value : checkbox.value;
    if (!variantId) return;
    lines.push({ id: Number(variantId), quantity: 1, properties: { _for_doll: dollId } });
  });
  return lines;
}

/**
 * @param {string} dollId
 * @param {Element} scope - the submitting form's own `#ProductInformation-…` element
 * @returns {{id: number, quantity: number, properties: {_for_doll: string}} | null}
 */
function collectCardLanguageLine(dollId, scope) {
  const root = scope.querySelector('[data-pt-card-message]');
  if (!(root instanceof HTMLElement)) return null;

  const languageInput = /** @type {HTMLInputElement | null} */ (root.querySelector('[data-pt-card-language-input]'));
  const language = languageInput?.value;
  if (!language || language === 'English') return null;

  const variantId = root.dataset.languageAddonVariantId;
  if (!variantId) return null;

  return { id: Number(variantId), quantity: 1, properties: { _for_doll: dollId } };
}

/**
 * Edit mode: the cart's "Edit" link opens this page with `?edit=<_doll_id>`.
 * Reads that doll and its add-ons from /cart.js (the cart is the source of
 * truth, so it works from any tab or device) and fills the page back in --
 * option, quantity, names, card, line-item properties and add-ons -- then
 * marks the page so the next add replaces those lines (see
 * {@link submitDollFormItems}). Does nothing when the doll is no longer in
 * the cart, leaving a normal product page.
 */
async function initEditMode() {
  const editId = new URLSearchParams(window.location.search).get('edit');
  const pickerRoot = /** @type {HTMLElement | null} */ (productScope?.querySelector('[data-pt-option-picker]') ?? null);
  if (!editId || !productScope || !pickerRoot) return;

  const formId = pickerRoot.dataset.productForm;
  const form = formId ? /** @type {HTMLFormElement | null} */ (document.getElementById(formId)) : null;
  if (!form) return;

  /** @type {{items: Array<{variant_id: number, quantity: number, properties: {[key: string]: string} | null, options_with_values: Array<{name: string, value: string}>}>}} */
  let cart;
  try {
    const response = await fetch('/cart.js', { credentials: 'same-origin' });
    cart = await response.json();
  } catch (error) {
    console.warn('[pt-product] could not load the cart to edit', error);
    return;
  }

  const dollLine = cart.items.find((line) => line.properties?._doll_id === editId);
  if (!dollLine) return;
  const addonLines = cart.items.filter((line) => line.properties?._for_doll === editId);
  const properties = dollLine.properties || {};

  // Option first: it drives face count (names) and rush eligibility (add-ons).
  dollLine.options_with_values.forEach((option, index) => {
    pickerRoot.querySelectorAll(`[data-pt-option-input][data-option-index="${index}"]`).forEach((input) => {
      const radio = /** @type {HTMLInputElement} */ (input);
      if (radio.value !== option.value || radio.checked) return;
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });

  if (setDollQuantity) {
    setDollQuantity(dollLine.quantity);
  } else {
    // No stepper on this page: keep the cart's quantity rather than resetting it to 1.
    currentQuantity = dollLine.quantity;
    refreshTotal();
  }

  restoreNames(properties.Name || '');
  restoreFormProperties(form, properties);
  restoreCardMessage(properties);
  restoreAddons(addonLines);

  editingDollId = editId;
  showEditNotice(form);
}

/**
 * Splits the joined `Name` property back over the visible name inputs; any
 * names beyond the input count stay together in the last input.
 * @param {string} joined
 */
function restoreNames(joined) {
  const root = productScope?.querySelector('[data-pt-names]');
  const inputs = /** @type {HTMLInputElement[]} */ (Array.from(root?.querySelectorAll('[data-pt-name-input]') ?? []));
  if (inputs.length === 0 || !joined) return;
  const names = joined.split(', ');
  inputs.forEach((input, index) => {
    input.value = index === inputs.length - 1 ? names.slice(index).join(', ') : names[index] || '';
    input.dispatchEvent(new Event('input', { bubbles: true }));
  });
}

/**
 * Writes saved line-item properties back onto the form's own `properties[…]`
 * fields (Notes, Photos later, In memory, an upload app's hidden fields…).
 * Name and the card fields are skipped: their widgets own them.
 * @param {HTMLFormElement} form
 * @param {{[key: string]: string}} properties
 */
function restoreFormProperties(form, properties) {
  const skipped = new Set(['Name', 'Card message', 'Card language']);
  Array.from(form.elements).forEach((element) => {
    if (!(element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement)) return;
    const key = element.name.match(PROPERTY_NAME_PATTERN)?.[1];
    if (!key || skipped.has(key) || element.type === 'file') return;
    const saved = properties[key];

    if (element instanceof HTMLInputElement && element.type === 'radio') {
      if (saved === undefined || element.value !== saved || element.checked) return;
      element.checked = true;
    } else if (element instanceof HTMLInputElement && element.type === 'checkbox') {
      const checked = saved !== undefined && saved === element.value;
      if (element.checked === checked) return;
      element.checked = checked;
    } else {
      element.value = saved ?? '';
    }
    element.dispatchEvent(new Event('change', { bubbles: true }));
  });
}

/**
 * Puts the saved card message and language back through the card modal's own
 * Save path, so the tile preview, hidden inputs and language add-on price all
 * update the same way a shopper's save does. A message equal to the card's
 * silent default was never typed by the shopper, so the modal stays empty.
 * @param {{[key: string]: string}} properties
 */
function restoreCardMessage(properties) {
  const root = productScope?.querySelector('[data-pt-card-message]');
  if (!root) return;
  const dialog = /** @type {HTMLElement | null} */ (root.querySelector('[data-pt-card-modal]'));
  const textarea = /** @type {HTMLTextAreaElement | null} */ (root.querySelector('[data-pt-card-textarea]'));
  const languageSelect = /** @type {HTMLSelectElement | null} */ (root.querySelector('[data-pt-card-language]'));
  const saveButton = /** @type {HTMLButtonElement | null} */ (root.querySelector('[data-pt-card-save]'));
  if (!textarea) return;

  const message = properties['Card message'] || '';
  textarea.value = message === dialog?.dataset.cardDefault ? '' : message;
  textarea.dispatchEvent(new Event('input', { bubbles: true }));
  if (languageSelect && properties['Card language']) languageSelect.value = properties['Card language'];
  saveButton?.click();
}

/**
 * Ticks exactly the add-ons this doll has in the cart -- including unticking
 * the segment's pre-checked default when the shopper had removed it. An
 * add-on hidden by the rush rule for the restored size stays unticked.
 * @param {Array<{variant_id: number}>} addonLines
 */
function restoreAddons(addonLines) {
  const root = productScope?.querySelector('[data-pt-make-it-special]');
  if (!root) return;
  const inCart = new Set(addonLines.map((line) => String(line.variant_id)));

  root.querySelectorAll('[data-pt-addon]').forEach((row) => {
    const checkbox = /** @type {HTMLInputElement | null} */ (row.querySelector('[data-pt-addon-checkbox]'));
    if (!checkbox) return;
    const select = /** @type {HTMLSelectElement | null} */ (row.querySelector('[data-pt-addon-variant]'));
    const savedOption = select ? Array.from(select.options).find((option) => inCart.has(option.value)) : null;
    if (select && savedOption) {
      select.value = savedOption.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const checked = !checkbox.disabled && (select ? Boolean(savedOption) : inCart.has(checkbox.value));
    if (checkbox.checked === checked) return;
    checkbox.checked = checked;
    checkbox.dispatchEvent(new Event('change', { bubbles: true }));
  });
}

/**
 * Tells the shopper they are editing a cart item: relabels the add buttons
 * and adds a short note with a way back to the cart unchanged.
 * @param {HTMLFormElement} form
 */
function showEditNotice(form) {
  form.querySelectorAll('button[name="add"]').forEach((button) => {
    const label = Array.from(button.childNodes).find(
      (node) => node.nodeType === Node.TEXT_NODE && (node.textContent || '').trim() !== ''
    );
    if (label) label.textContent = 'Update cart';
  });

  const cartUrl = (typeof Theme !== 'undefined' && Theme.routes?.cart_url) || '/cart';
  const notice = document.createElement('p');
  notice.className = 'pt-edit-notice';
  notice.append('Editing a doll in your cart. Saving replaces it. ');
  const back = document.createElement('a');
  back.href = cartUrl;
  back.textContent = 'Back to cart';
  notice.append(back);
  form.before(notice);
}

/**
 * Shows an inline error using Horizon's existing add-to-cart error slot.
 * @param {HTMLFormElement} form
 * @param {string} message
 */
function showFormError(form, message) {
  const errorEl = form.querySelector('[ref="addToCartTextError"]');
  if (!errorEl) {
    console.error('[pt-product]', message);
    return;
  }
  errorEl.classList.remove('hidden');
  errorEl.setAttribute('aria-live', 'assertive');
  const textNode = Array.from(errorEl.childNodes).find((node) => node.nodeType === Node.TEXT_NODE);
  if (textNode) {
    textNode.textContent = message;
  } else {
    errorEl.appendChild(document.createTextNode(message));
  }
}

/**
 * Validates and collects the doll form into cart-add `items`, exactly like
 * the normal submit path, but without touching the DOM's disabled state or
 * navigating anywhere -- shared by {@link handleSubmit} (redirects to
 * /cart) and the "Buy with shop" button (redirects to /checkout instead of
 * running Shopify's native accelerated checkout, which would drop every
 * pt-* property/add-on since it never goes through this collection step).
 * @param {HTMLFormElement} form
 * @param {Element} scope - the submitting form's own `#ProductInformation-…` element
 * @param {HTMLElement | null} pickerRoot
 * @returns {{ok: true, items: Array<{id: number, quantity: number, properties?: {[key: string]: string}}>} | {ok: false}}
 */
function prepareDollFormItems(form, scope, pickerRoot) {
  const cardMessageRoot = /** @type {HTMLElement | null} */ (scope.querySelector('[data-pt-card-message]'));
  if (cardMessageRoot) {
    const messageInput = /** @type {HTMLInputElement | null} */ (
      cardMessageRoot.querySelector('[data-pt-card-message-input]')
    );
    const dialog = cardMessageRoot.querySelector('[data-pt-card-modal]');
    if (messageInput && !messageInput.value.trim()) {
      messageInput.value = /** @type {HTMLElement | null} */ (dialog)?.dataset.cardDefault || '';
    }
  }

  if (!validatePhotoUpload(form).ok) return { ok: false };

  const idInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="id"]'));
  const variantId = idInput?.value;
  if (!variantId) {
    showFormError(form, "Please choose an option before adding to cart.");
    return { ok: false };
  }

  const dollId = crypto.randomUUID().slice(0, 8);
  const properties = collectDollProperties(form);
  properties._doll_id = dollId;
  if (pickerRoot?.dataset.segment) properties._segment = pickerRoot.dataset.segment;

  const items = [{ id: Number(variantId), quantity: currentQuantity, properties }, ...collectAddonLines(dollId, scope)];
  const languageLine = collectCardLanguageLine(dollId, scope);
  if (languageLine) items.push(languageLine);

  return { ok: true, items };
}

/**
 * Adds `items` to the cart via the same AJAX endpoint + cart-update event
 * bus as Horizon's own forms, then navigates to `redirectUrl`.
 * @param {HTMLFormElement} form
 * @param {Array<{id: number, quantity: number, properties?: {[key: string]: string}}>} items
 * @param {HTMLButtonElement[]} buttonsToDisable
 * @param {string} redirectUrl
 */
async function submitDollFormItems(form, items, buttonsToDisable, redirectUrl) {
  buttonsToDisable.forEach((button) => {
    button.disabled = true;
  });

  const deferredEventPromise = CartLinesUpdateEvent.createPromise();
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferredEventPromise.promise,
    })
  );

  /** @type {{items: Array<{[key: string]: unknown}>, status?: unknown, message?: string, description?: string}} */
  let addData;
  try {
    const response = await fetch(
      Theme.routes.cart_add_url,
      fetchConfig('json', { body: JSON.stringify({ items }) })
    );
    addData = await response.json();

    if (!response.ok || addData.status) {
      throw new Error(addData.message || addData.description || 'Add to cart failed');
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Something went wrong. Please try again.';
    console.error('[pt-product] add to cart failed', error);
    deferredEventPromise.reject(error);
    form.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
    showFormError(form, message);
    buttonsToDisable.forEach((button) => {
      button.disabled = false;
    });
    return;
  }

  // The add already succeeded — the items are in the cart. From here on a
  // retry would add them a second time, so the buttons stay disabled no
  // matter what happens next, and the shopper is always sent to
  // `redirectUrl` even if the refetch below (used only to resolve the
  // deferred cart-update event) fails.
  //
  // Editing a doll: the new lines are in, so the old doll and its add-ons go.
  // Add-then-remove means a failure here leaves a duplicate the shopper can
  // remove in the cart, never a lost doll.
  // Lines are found again by `_doll_id`/`_for_doll` on a fresh cart: Shopify
  // re-keys existing lines when others are added, so keys read on page load
  // are stale by now and could point at the lines just added.
  if (editingDollId) {
    try {
      const cartResponse = await fetch('/cart.js', { credentials: 'same-origin' });
      /** @type {{items: Array<{key: string, properties: {[key: string]: string} | null}>}} */
      const cart = await cartResponse.json();
      const updates = Object.fromEntries(
        cart.items
          .filter((line) => line.properties?._doll_id === editingDollId || line.properties?._for_doll === editingDollId)
          .map((line) => [line.key, 0])
      );
      const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body: JSON.stringify({ updates }) }));
      if (!response.ok) throw new Error(`cart update ${response.status}`);
    } catch (error) {
      console.warn('[pt-product] removing the edited doll failed', error);
    }
  }

  try {
    const cartResponse = await fetch('/cart.js', { credentials: 'same-origin' });
    const cart = await cartResponse.json();
    deferredEventPromise.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: addData.items, source: 'pt-product', didError: false },
    });
  } catch (error) {
    console.warn('[pt-product] cart refetch after add failed', error);
    deferredEventPromise.reject(error instanceof Error ? error : new Error('Cart refetch failed'));
  }

  window.location.href = redirectUrl;
}

/**
 * Capture-phase submit handler: only acts on the buy form of a pt-* product
 * page (marked by `[data-pt-marker]`) and never on gift cards.
 * @param {SubmitEvent} event
 */
async function handleSubmit(event) {
  const form = event.target;
  // getAttribute: the form's <input name="id"> shadows form.id.
  if (!(form instanceof HTMLFormElement) || !(form.getAttribute('id') || '').startsWith('BuyButtons-ProductForm-')) return;

  const scope = form.closest('[id^="ProductInformation-"]');
  if (!scope || !scope.querySelector('[data-pt-marker]')) return;

  const pickerRoot = /** @type {HTMLElement | null} */ (scope.querySelector('[data-pt-option-picker]'));
  if (pickerRoot?.dataset.giftCard === 'true') return;

  event.preventDefault();
  event.stopImmediatePropagation();

  const prepared = prepareDollFormItems(form, scope, pickerRoot);
  if (!prepared.ok) return;

  const submitButtons = /** @type {HTMLButtonElement[]} */ (Array.from(form.querySelectorAll('button[name="add"]')));
  const cartUrl = (typeof Theme !== 'undefined' && Theme.routes?.cart_url) || '/cart';
  await submitDollFormItems(form, prepared.items, submitButtons, cartUrl);
}

/**
 * "Buy with shop" button (blocks/pt-buy-with-shop.liquid): runs the exact
 * same validation/collection/cart-add as the normal Add to cart submit, then
 * goes straight to /checkout instead of /cart. Deliberately not Shopify's
 * native accelerated-checkout component (disabled in templates/product.json)
 * -- that component posts only the selected variant, so every pt-* property
 * and add-on line this page collects would be silently dropped.
 * @param {MouseEvent} event
 */
async function handleBuyWithShopClick(event) {
  const button = /** @type {HTMLButtonElement | null} */ (event.currentTarget instanceof HTMLButtonElement ? event.currentTarget : null);
  if (!button) return;

  const form = /** @type {HTMLFormElement | null} */ (
    button.form || (button.getAttribute('form') ? document.getElementById(button.getAttribute('form') || '') : null)
  );
  if (!(form instanceof HTMLFormElement)) return;

  const scope = form.closest('[id^="ProductInformation-"]');
  if (!scope || !scope.querySelector('[data-pt-marker]')) return;

  const pickerRoot = /** @type {HTMLElement | null} */ (scope.querySelector('[data-pt-option-picker]'));
  if (pickerRoot?.dataset.giftCard === 'true') return;

  const prepared = prepareDollFormItems(form, scope, pickerRoot);
  if (!prepared.ok) return;

  const submitButtons = /** @type {HTMLButtonElement[]} */ (
    Array.from(form.querySelectorAll('button[name="add"]')).concat(button)
  );
  await submitDollFormItems(form, prepared.items, submitButtons, '/checkout');
}

/**
 * Overlays K's static gallery badges -- "Best gift" + "Photo ↔ Doll" for
 * dolls, "Made from your photo" for everything else (ornaments, totes,
 * car hangers...) -- onto Horizon's native media gallery. The gallery and
 * the option picker are sibling blocks of the same product-information
 * section, not nested inside each other, so this walks up to the shared
 * `.shopify-section` instead of using `productScope`
 * (`#ProductInformation-*` only wraps the details column).
 */
function initGalleryBadges() {
  const pickerRoot = /** @type {HTMLElement | null} */ (document.querySelector('[data-pt-option-picker]'));
  if (!pickerRoot) return;

  const section = pickerRoot.closest('.shopify-section');
  // `.product-media` (not `media-gallery` or `.product-media-container`) is
  // the tightest wrapper that always matches the rendered image's own box --
  // the wider ancestors reserve the full slide/column width, which floats
  // the badge in the gutter beside the image instead of over it whenever the
  // image itself renders narrower (e.g. with `constrain_to_viewport` on).
  const mediaContainer = section?.querySelector('media-gallery .product-media');
  if (!mediaContainer || mediaContainer.querySelector('.pt-gallery-badge')) return;

  const primary = document.createElement('span');
  primary.className = 'pt-gallery-badge pt-gallery-badge--primary';

  if (pickerRoot.dataset.productType === 'Doll') {
    primary.textContent = 'Best gift';
    const secondary = document.createElement('span');
    secondary.className = 'pt-gallery-badge pt-gallery-badge--secondary';
    secondary.textContent = 'Photo ↔ Doll';
    mediaContainer.append(primary, secondary);
  } else {
    primary.textContent = 'Made from your photo';
    mediaContainer.append(primary);
  }
}

function init() {
  // Resolve the product's own scope once, before any of the pt-* widgets
  // wire themselves up, so every one of them (and refreshTotal()) reads and
  // writes only inside this product's section — never `document`-wide. When
  // no pt-option-picker rendered at all (gift cards, Add-on products), this
  // stays null and every init function below becomes a no-op.
  productScope = document.querySelector('[data-pt-option-picker]')?.closest('[id^="ProductInformation-"]') ?? null;

  // initOptionPicker() dispatches the initial `pt:variant-change` synchronously
  // (see applyVariant()), so every listener for it — initMakeItSpecial's rush
  // guard included — must be registered first, or the first dispatch is
  // missed and rush visibility is wrong until the shopper changes an option.
  initNames();
  initPhotoUpload();
  initCardMessage();
  initMakeItSpecial();
  initDeliveryLine();
  initOptionPicker();
  initGalleryBadges();
  initBuyWithShop();
  // After every widget is wired: edit mode replays the saved choices through
  // those widgets' own change/input handlers.
  initEditMode();
}

/** Wires every "Buy with shop" button on the page (see handleBuyWithShopClick). */
function initBuyWithShop() {
  document.querySelectorAll('[data-pt-buy-with-shop]').forEach((button) => {
    button.addEventListener('click', handleBuyWithShopClick);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Capture phase on `window`, not `document`: Horizon's own component.js also
// registers its `on:submit` dispatcher on `document` capture, at head-module
// load time — earlier than this deferred module runs. Two capture listeners
// on the *same* target (document) always run in registration order, so a
// document-capture listener here would fire after Horizon's and could never
// stop it with stopImmediatePropagation(). `window` is an ancestor of
// `document` in the capture path, so a window-capture listener always runs
// first regardless of script load order.
window.addEventListener('submit', handleSubmit, true);
